import { useInsforge } from '../provider/InsforgeProvider';
import type { ReactNode } from 'react';

export interface ConditionalProps {
  children: ReactNode;
}

/**
 * Conditional component that renders children only when user is signed in.
 *
 * Uses userId to determine rendering:
 * - undefined = not loaded yet (SSR or before hydration) -> render null
 * - null = loaded, user signed out -> render null
 * - string = loaded, user signed in -> render children
 *
 * This prevents hydration mismatches in Next.js SSR by ensuring
 * consistent rendering between server and client initial render.
 *
 * @component
 * @example
 * ```tsx
 * <SignedIn>
 *   <Dashboard />
 * </SignedIn>
 * ```
 *
 * @param {ReactNode} children - React nodes to render when user is authenticated
 * @returns {JSX.Element | null} Renders children when signed in, null otherwise
 */
export function SignedIn({ children }: ConditionalProps) {
  const { userId } = useInsforge();

  // undefined = not loaded yet, render null (prevents hydration mismatch)
  // null = loaded but signed out, render null
  // string = signed in, render children
  if (userId) {
    return <>{children}</>;
  }

  return null;
}
